import React, { useState, useEffect } from 'react';
import SEO from '../components/SEO';
import ContactForm from '../components/ContactForm';
import { useLogoTheme } from '../context/LogoThemeContext';
import { fetchSingleItem } from '../lib/cms';

export default function ContactPage() {
  const { headerBgColor } = useLogoTheme();
  const [kontaktData, setKontaktData] = useState(null);

  useEffect(() => {
    async function loadContact() {
      const data = await fetchSingleItem('kontakt_oplysninger');
      if (data) setKontaktData(data);
    }
    loadContact();
  }, []);

  const firmanavn = kontaktData?.firmanavn || 'Lenea Evolinee';
  const intro = kontaktData?.kontakt_intro || 'Har du spørgsmål til mine forløb, behandlinger eller ønsker du at booke en afklarende samtale? Udfyld formularen eller skriv direkte til mig.';
  const adresse = kontaktData?.adresse ? `${kontaktData.adresse}, ${kontaktData.postnr_by || '3520 Farum'}` : 'Ganløsevej 38, 3520 Farum';
  const telefon = kontaktData?.telefon || '24 91 43 13';
  const telefonClean = telefon.replace(/\s+/g, '');
  const email = kontaktData?.email || 'kontakt@leneaevolinee.dk';
  const cvr = kontaktData?.cvr || '31679478';

  return (
    <>
      <SEO
        title={`Kontakt ${firmanavn}`}
        description={`Kontakt ${firmanavn} i Farum. Adresse: ${adresse}. Tlf: ${telefon}, Mail: ${email}. Book en uforpligtende samtale i dag.`}
      />

      {/* Hero Header */}
      <section style={{ backgroundColor: headerBgColor }} className="pt-36 pb-20 transition-colors duration-300">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4">
          <span className="text-xs uppercase tracking-widest text-[#98866C] font-semibold block">Jeg glæder mig til at tale med dig</span>
          <h1 className="font-serif text-4xl sm:text-5xl font-normal text-[#2E2A27]">
            Kontakt {firmanavn}
          </h1>
          <p className="text-base sm:text-lg text-[#5C524A] leading-relaxed max-w-2xl mx-auto font-sans">
            {intro}
          </p>
        </div>
      </section>

      {/* Main Grid: Form + Info */}
      <section className="py-20 bg-white border-y border-[#E8DFD5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Contact Details Column */}
            <div className="lg:col-span-5 space-y-8 bg-[#FAF7F2] p-8 sm:p-10 rounded-sm border border-[#D8CDBF] shadow-xs">
              <div>
                <h2 className="font-serif text-2xl font-normal text-[#2E2A27] mb-2">
                  Klinikken i Farum
                </h2>
                <p className="text-sm text-[#5C524A] leading-relaxed font-sans">
                  {firmanavn} holder til i rolige og naturskønne omgivelser i Farum. Der er gratis og nem parkering direkte ved døren.
                </p>
              </div>

              <div className="space-y-4 pt-4 border-t border-[#E8DFD5] text-base text-[#2E2A27] font-sans">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#98866C]/20 text-[#98866C] flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-location-dot text-base" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="text-[11px] font-semibold uppercase tracking-widest text-[#98866C] block">Adresse</span>
                    <span className="font-medium">{adresse}</span>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#98866C]/20 text-[#98866C] flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-phone text-base" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="text-[11px] font-semibold uppercase tracking-widest text-[#98866C] block">Telefon</span>
                    <a href={`tel:${telefonClean}`} className="font-medium hover:text-[#98866C] whitespace-nowrap cursor-pointer">
                      {telefon}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#98866C]/20 text-[#98866C] flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-envelope text-base" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="text-[11px] font-semibold uppercase tracking-widest text-[#98866C] block">E-mail</span>
                    <a href={`mailto:${email}`} className="font-medium hover:text-[#98866C] whitespace-nowrap cursor-pointer">
                      {email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#98866C]/20 text-[#98866C] flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-id-card text-base" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="text-[11px] font-semibold uppercase tracking-widest text-[#98866C] block">Virksomhed</span>
                    <span className="font-medium">CVR: {cvr}</span>
                  </div>
                </div>
              </div>

              {/* Hours / Note */}
              <div className="p-4 rounded-sm bg-white border border-[#D8CDBF] text-xs text-[#5C524A] space-y-1 font-sans">
                <span className="font-semibold text-[#2E2A27] block">Åbningstider & telefontid</span>
                <p>Konsultationer ifølge aftale. Jeg bestræber mig på at besvare alle henvendelser inden for 24 timer på hverdage.</p>
              </div>
            </div>

            {/* Contact Form Column */}
            <div className="lg:col-span-7">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      {/* Google Maps Embed Section */}
      <section className="py-16 bg-[#FAF7F2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          <div className="text-center max-w-2xl mx-auto space-y-2">
            <span className="text-xs uppercase tracking-widest text-[#98866C] font-semibold block">
              Find vej
            </span>
            <h2 className="font-serif text-3xl font-normal text-[#2E2A27]">
              Kort over klinikken
            </h2>
          </div>

          <div className="rounded-sm overflow-hidden border border-[#D8CDBF] shadow-xs aspect-21/9 bg-[#FAF7F2]">
            <iframe
              title="Lenea Evolinee Google Map Farum"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2240.245802187654!2d12.339185!3d55.807892!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x465245a9a13bbf79%3A0x6b4a20a6e0c0!2sGanl%C3%B8sevej%2038%2C%203520%20Farum!5e0!3m2!1sda!2sdk!4v1710000000000!5m2!1sda!2sdk"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>
    </>
  );
}
